import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "./ui/alert-dialog";
import { CheckCircle, DownloadSimple, Eye, WarningCircle } from "@phosphor-icons/react";
import { STAGE_META, money } from "../pages/insurance/InsuranceList";

const ACTION_META = {
  create: { label: "Create", cls: "text-emerald-300 border-emerald-500/30 bg-emerald-500/10" },
  skip_duplicate: { label: "Duplicate — skipped", cls: "text-amber-300 border-amber-500/30 bg-amber-500/10" },
  error: { label: "Error — skipped", cls: "text-red-300 border-red-500/30 bg-red-500/10" },
};

const CSV_HEAD = [
  "row", "action", "duplicate_of", "case_number", "client", "company", "claim_number",
  "rental_start", "rental_end", "authorised_until", "stage", "tracker_amount", "daily_rate",
  "our_cut", "affiliate_amount", "affiliate_paid", "referral_amount", "referral_name",
  "vehicle_provided", "client_vehicle", "adjuster_email", "adjuster_phone",
  "inferred_years", "warnings",
];

const csvCell = (v) => `"${String(v ?? "").replace(/"/g, '""').replace(/\s*\n\s*/g, " · ")}"`;

const csvRow = (r) => {
  const c = r.case || {}, s = c.split || {}, cov = c.coverage || {};
  return [
    r.row, r.action, r.duplicate_of || "", r.case_number || "", c.client_name, c.insurance_company,
    c.claim_number, c.rental_start, c.rental_end, cov.approved_until, c.stage, c.import_amount,
    cov.base_daily_rate, s.our_cut, s.affiliate_amount, s.affiliate_paid ? "Y" : "N",
    s.referral_amount, s.referral_name, c.assigned_vehicle_label, c.client_vehicle,
    c.adjuster_email, c.adjuster_phone, (c.import_inferred_years || []).join(" "),
    (r.warnings || []).concat(r.error ? [r.error] : []).join(" · "),
  ].map(csvCell).join(",");
};

const Inferred = () => (
  <span className="ml-1 text-[10px] uppercase tracking-wider px-1 py-px border border-amber-500/40 text-amber-300 align-middle">
    inferred
  </span>
);

const PreviewRow = ({ r }) => {
  const c = r.case || {};
  const inferred = c.import_inferred_years || [];
  const action = ACTION_META[r.action] || ACTION_META.error;
  const stage = STAGE_META[c.stage];
  const reasons = (r.warnings || []).concat(r.error ? [r.error] : []);
  return (
    <tr className="border-b border-border/50 last:border-b-0 align-top" data-testid={`insurance-import-row-${r.row}`}>
      <td className="px-3 py-3 tnum text-muted-foreground">{r.row}</td>
      <td className="px-3 py-3">
        <span className={`inline-block whitespace-nowrap text-[11px] px-1.5 py-0.5 border ${action.cls}`} data-testid={`insurance-import-action-${r.row}`}>
          {action.label}
        </span>
        {r.case_number && <span className="block mt-1 font-heading text-[13px] whitespace-nowrap">{r.case_number}</span>}
        {r.duplicate_of && <span className="block text-[11px] text-muted-foreground whitespace-nowrap">of {r.duplicate_of}</span>}
      </td>
      <td className="px-3 py-3 whitespace-nowrap">{c.client_name || <span className="text-muted-foreground">—</span>}</td>
      <td className="px-3 py-3 text-muted-foreground whitespace-nowrap">{c.insurance_company || "—"}</td>
      <td className="px-3 py-3 tnum whitespace-nowrap">{c.claim_number || <span className="text-muted-foreground">none</span>}</td>
      <td className="px-3 py-3 whitespace-nowrap tnum" data-testid={`insurance-import-dates-${r.row}`}>
        {c.rental_start || "—"}{inferred.includes("start") && <Inferred />}
        <span className="text-muted-foreground"> → </span>
        {c.rental_end || "—"}{inferred.includes("end") && <Inferred />}
      </td>
      <td className="px-3 py-3">
        {stage ? <span className={`inline-block whitespace-nowrap text-[11px] px-1.5 py-0.5 border ${stage.cls}`}>{stage.label}</span> : "—"}
      </td>
      <td className="px-3 py-3 text-right money whitespace-nowrap">
        {c.import_amount || c.import_amount === 0 ? money(c.import_amount) : <span className="text-muted-foreground">—</span>}
      </td>
      <td className="px-3 py-3 text-[12px] text-muted-foreground min-w-[280px]" data-testid={`insurance-import-warnings-${r.row}`}>
        {reasons.length === 0 ? "" : <ul className="space-y-0.5 leading-snug">{reasons.map((w, i) => <li key={i}>{w}</li>)}</ul>}
      </td>
    </tr>
  );
};

export const InsuranceImportPanel = ({ file }) => {
  const [plan, setPlan] = useState(null);
  const [busy, setBusy] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => { setPlan(null); setDone(null); }, [file]);

  const send = async (path) => {
    const form = new FormData();
    form.append("file", file);
    const { data } = await api.post(`/insurance/import/${path}`, form, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return data;
  };

  const preview = async () => {
    setBusy("preview");
    try {
      const data = await send("preview");
      setPlan(data);
      setDone(null);
      toast.success(`Dry run complete — ${data.summary.create} to create, nothing written`);
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(""); }
  };

  const commit = async () => {
    setBusy("commit");
    setConfirming(false);
    try {
      const data = await send("commit");
      setPlan(data);
      setDone(data);
      toast.success(`Imported ${data.summary.created} cases`);
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(""); }
  };

  const download = () => {
    const csv = [CSV_HEAD.join(","), ...plan.rows.map(csvRow)].join("\r\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8;" }));
    const a = document.createElement("a");
    a.href = url;
    // After a commit the same table is the record of what was created, not a
    // dry run — so it must not be filed away as "preview".
    a.download = done ? "insurance-import-result.csv" : "insurance-import-preview.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const s = plan?.summary;

  return (
    <div className="space-y-6" data-testid="insurance-import-panel">
      <div className="flex flex-wrap items-center gap-3">
        <Button onClick={preview} disabled={!file || !!busy} data-testid="insurance-import-preview-button"
          className="bg-primary text-primary-foreground hover:bg-primary/90">
          <Eye size={16} className="mr-2" />{busy === "preview" ? "Reading the file…" : "Preview (dry run)"}
        </Button>
        <p className="text-xs text-muted-foreground">
          A dry run writes nothing. Read the table, then confirm.
        </p>
      </div>

      {plan && (
        <div className="border border-border bg-card">
          <div className="p-5 border-b border-border flex flex-wrap items-center justify-between gap-4">
            <div>
              <div className="label-mono">{done ? "Import complete" : "Dry run"}</div>
              <p className="font-heading text-lg mt-1" data-testid="insurance-import-summary">
                {done ? `Created ${s.created}` : `Will create ${s.create}`}
                <span className="text-muted-foreground"> · </span>Skip {s.skip_duplicate} duplicate{s.skip_duplicate === 1 ? "" : "s"}
                <span className="text-muted-foreground"> · </span>{s.error} error{s.error === 1 ? "" : "s"}
                <span className="text-muted-foreground"> · </span>{s.warnings} warning{s.warnings === 1 ? "" : "s"}
              </p>
              {Object.keys(s.inferred_start_years || {}).length > 0 && (
                <p className="text-xs text-muted-foreground mt-1.5" data-testid="insurance-import-inferred-breakdown">
                  Inferred start years: {Object.entries(s.inferred_start_years).map(([y, n]) => `${y} × ${n}`).join(" · ")}
                  {s.capped ? ` · ${s.capped} held back to this year` : ""}
                </p>
              )}
            </div>
            <div className="flex items-center gap-3">
              <Button variant="outline" onClick={download} data-testid="insurance-import-download-button">
                <DownloadSimple size={16} className="mr-2" />Download {done ? "result" : "preview"} as CSV
              </Button>
              {done ? (
                <Button asChild className="bg-primary text-primary-foreground hover:bg-primary/90">
                  <Link to="/insurance" data-testid="insurance-import-view-cases-link">Open insurance cases</Link>
                </Button>
              ) : (
                <Button onClick={() => setConfirming(true)} disabled={!s.create || !!busy}
                  className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="insurance-import-commit-button">
                  {busy === "commit" ? "Importing…" : `Import ${s.create} case${s.create === 1 ? "" : "s"}`}
                </Button>
              )}
            </div>
          </div>

          {done && (
            <div className="px-5 py-4 border-b border-border flex items-center gap-2 text-sm" data-testid="insurance-import-result">
              <CheckCircle size={18} weight="fill" className="text-primary shrink-0" />
              {s.created} case{s.created === 1 ? "" : "s"} created
              {done.created_case_numbers?.length ? ` — ${done.created_case_numbers.slice(0, 3).join(", ")}${done.created_case_numbers.length > 3 ? "…" : ""}` : ""}.
              Follow-up is off on every one of them.
            </div>
          )}

          <div className="max-h-[30rem] overflow-auto">
            <table className="w-full min-w-[1100px] text-[13px]" data-testid="insurance-import-table">
              <thead className="sticky top-0 bg-card">
                <tr className="border-b border-border text-left">
                  <th scope="col" className="px-3 py-3 label-mono font-normal">Row</th>
                  <th scope="col" className="px-3 py-3 label-mono font-normal">Action</th>
                  <th scope="col" className="px-3 py-3 label-mono font-normal">Client</th>
                  <th scope="col" className="px-3 py-3 label-mono font-normal">Company</th>
                  <th scope="col" className="px-3 py-3 label-mono font-normal">Claim #</th>
                  <th scope="col" className="px-3 py-3 label-mono font-normal">Start → End</th>
                  <th scope="col" className="px-3 py-3 label-mono font-normal">Stage</th>
                  <th scope="col" className="px-3 py-3 label-mono font-normal text-right">Tracker amount</th>
                  <th scope="col" className="px-3 py-3 label-mono font-normal">Warnings &amp; reasons</th>
                </tr>
              </thead>
              <tbody>{plan.rows.map((r) => <PreviewRow key={r.row} r={r} />)}</tbody>
            </table>
          </div>
        </div>
      )}

      <AlertDialog open={confirming} onOpenChange={setConfirming}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Import {s?.create} case{s?.create === 1 ? "" : "s"}?</AlertDialogTitle>
            <AlertDialogDescription>
              {s?.create} case{s?.create === 1 ? "" : "s"} will be created with numbers keyed to their start year.
              {s?.skip_duplicate ? ` ${s.skip_duplicate} duplicate row${s.skip_duplicate === 1 ? "" : "s"} will be skipped.` : ""}
              {s?.error ? ` ${s.error} row${s.error === 1 ? "" : "s"} could not be read and will be skipped.` : ""}
              {" "}No bookings, invoices or emails — follow-up stays off on every imported case.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="insurance-import-cancel-button">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={commit} data-testid="insurance-import-confirm-button">
              Import {s?.create} case{s?.create === 1 ? "" : "s"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {plan && s.error > 0 && (
        <p className="text-xs text-muted-foreground flex items-start gap-2">
          <WarningCircle size={14} className="mt-0.5 shrink-0" />
          Rows marked with an error are skipped, never guessed at. Fix them in the sheet and import that file again — anything already in the CRM is detected as a duplicate.
        </p>
      )}
    </div>
  );
};
